import {ActionWithPayload, batch} from '../../../../../helpers/reduxHelpers';
import {ThunkAction} from '../../../../appThunk';
import {PanelActionPayload} from '../../../panelsActions';
import {getPanels} from '../../../panelsSelectors';
import {panelIsContactsManagerPanel} from '../../../panelsState';
import {ContactsManagerPanelId} from '../../../panelStateBase';
import {makeGetContactsManagerPanel} from '../contactsManagerPanelSelectors';
import {ContactsManagerSearchTabsEnum} from '../parameters/contactsManagerParameters';
import {closeSidePanelAction} from '../sidePanel/contactsManagerSidePanelActions';
import {contactsManagerTableAccountIdsRemoveSelectedAction} from '../table/contactsManagerTableActions';

/*
 * Type enum.
 */

export enum ContactsManagerSearchActionsEnum {
  TAB_SELECTED = '@@panels/contactsManager/search/TAB_SELECTED',
  CLEAN_UP = '@@panels/contactsManager/search/CLEAN_UP',

  TABLES_FORCE_RELOADED = '@@panels/contactsManager/search/TABLES_FORCE_RELOADED',
}

/*
 * Types.
 */

type ContactsManagerPanelActionPayload = PanelActionPayload<ContactsManagerPanelId>;

interface TabSelectedActionPayload extends ContactsManagerPanelActionPayload {
  tab: ContactsManagerSearchTabsEnum;
}

interface TabSelectedAction extends ActionWithPayload<TabSelectedActionPayload> {
  type: ContactsManagerSearchActionsEnum.TAB_SELECTED;
}

interface CleanUpAction extends ActionWithPayload<ContactsManagerPanelActionPayload> {
  type: ContactsManagerSearchActionsEnum.CLEAN_UP;
}

interface TablesForceReloadedAction extends ActionWithPayload<ContactsManagerPanelActionPayload> {
  type: ContactsManagerSearchActionsEnum.TABLES_FORCE_RELOADED;
}

/*
 * Action creators.
 */

export function tabSelectedAction(
  panelId: ContactsManagerPanelId,
  tab: ContactsManagerSearchTabsEnum,
): TabSelectedAction {
  return {
    type: ContactsManagerSearchActionsEnum.TAB_SELECTED,
    payload: {
      panelId,
      tab,
    },
  };
}

export function contactsManagerSearchPageCleanUpAction(panelId: ContactsManagerPanelId): CleanUpAction {
  return {
    type: ContactsManagerSearchActionsEnum.CLEAN_UP,
    payload: {
      panelId,
    },
  };
}

export function tablesForceReloadedAction(panelId: ContactsManagerPanelId): TablesForceReloadedAction {
  return {
    type: ContactsManagerSearchActionsEnum.TABLES_FORCE_RELOADED,
    payload: {
      panelId,
    },
  };
}

/*
 * Actions.
 */

export function contactsManagerSearchPageTabSelectedAction(
  panelId: ContactsManagerPanelId,
  tab: ContactsManagerSearchTabsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panelIsContactsManagerPanel(panel)) {
      return;
    }

    // If we already have the tab selected, this is a noop.
    if (panel.searchState?.selectedTab === tab) {
      return;
    }

    dispatch(tabSelectedAction(panelId, tab));
  };
}

export function contactsManagerSearchTablesForceReloadedAction(panelId: ContactsManagerPanelId): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {searchState} = contactsManagerPanel;

    if (!searchState) {
      return;
    }

    dispatch(tablesForceReloadedAction(panelId));
  };
}

export function contactsManagerSearchAccountsCloseSidePanelAndRemoveSelectedAction(
  panelId: ContactsManagerPanelId,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!contactsManagerPanel) {
      return;
    }

    const {searchState} = contactsManagerPanel;

    batch(() => {
      dispatch(closeSidePanelAction());
      dispatch(
        contactsManagerTableAccountIdsRemoveSelectedAction(
          panelId,
          searchState?.accountsTableState?.selectedAccountIds ?? [],
        ),
      );
    });
  };
}

/*
 * Union type.
 */

export type ContactsManagerSearchAction = TabSelectedAction | CleanUpAction | TablesForceReloadedAction;
